import React, { useState, useMemo } from 'react';
import { Card } from '@components/atoms/Card';
import { Loading } from '@components/atoms/Loading';
import { Modal } from '@components/molecules/Modal';
import { OrganizationSubscription } from '@models/subscription';
import { Package, Plus, Trash2, Calendar, Users, DollarSign, AlertTriangle } from 'lucide-react';
import { useRemoveAddonSubscription, getRemoveAddonErrorMessage } from '@hooks/api/subscription/useRemoveAddonSubscription';
import { useActiveSeats } from '@hooks/api/subscription/useActiveSeats';
import { AddAddonModal } from '../../users/components/AddAddonModal';
import { useTranslation } from '@hooks/useTranslation';

interface OrganizationSeatsProps {
  baseSubscription: OrganizationSubscription | null | undefined;
  addonSubscriptions?: OrganizationSubscription[];
  totalIncludedSeats?: number;
  isLoading?: boolean;
}

const OrganizationSeats: React.FC<OrganizationSeatsProps> = ({
  baseSubscription,
  addonSubscriptions = [],
  totalIncludedSeats,
  isLoading = false,
}) => {
  const t = useTranslation('billing');
  const [showAddAddonModal, setShowAddAddonModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [addonToRemove, setAddonToRemove] = useState<OrganizationSubscription | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { mutateAsync: removeAddon, isPending: isRemoving } = useRemoveAddonSubscription();
  const { data: activeSeats = [] } = useActiveSeats();

  // Calculate if addon can be removed based on active seats
  // Note: activeSeats excludes the OWNER (covered by base plan), so we need to calculate
  // available seats for non-owner users: addon seats only (base plan seat is for owner)
  const canRemoveAddon = useMemo(() => {
    if (!addonToRemove) return true;

    // Calculate seats from OTHER addons (excluding the one being removed)
    const otherAddonSeats = addonSubscriptions
      .filter((addon) => addon.id !== addonToRemove.id)
      .reduce((sum, addon) => sum + addon.includedSeats, 0);

    const activeUsersCount = activeSeats.length;

    // Can remove if remaining addon seats can cover active users (non-owners)
    return activeUsersCount <= otherAddonSeats;
  }, [addonToRemove, addonSubscriptions, activeSeats]);

  const seatsNeededToRemove = useMemo(() => {
    if (!addonToRemove) return 0;

    const otherAddonSeats = addonSubscriptions
      .filter((addon) => addon.id !== addonToRemove.id)
      .reduce((sum, addon) => sum + addon.includedSeats, 0);

    const activeUsersCount = activeSeats.length;

    return Math.max(0, activeUsersCount - otherAddonSeats);
  }, [addonToRemove, addonSubscriptions, activeSeats]);

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amountInCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amountInCents / 100);
  };

  const handleRemoveClick = (addon: OrganizationSubscription) => {
    setAddonToRemove(addon);
    setRemoveError(null);
    setShowRemoveModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!addonToRemove) return;

    try {
      setRemoveError(null);
      await removeAddon(addonToRemove.id);
      setShowRemoveModal(false);
      setAddonToRemove(null);
    } catch (error) {
      setRemoveError(getRemoveAddonErrorMessage(error));
    }
  };

  const handleCancelRemove = () => {
    setShowRemoveModal(false);
    setAddonToRemove(null);
    setRemoveError(null);
  };

  if (isLoading) {
    return (
      <Card>
        <div className="py-12">
          <Loading size="lg" center />
        </div>
      </Card>
    );
  }

  if (!baseSubscription) {
    return (
      <Card>
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">{t.seats.noSubscriptionFound}</p>
        </div>
      </Card>
    );
  }

  const basePlanSeats = baseSubscription.includedSeats;
  const basePriceCents = baseSubscription.planSnapshot?.basePriceCents || 0;
  const addonSeats = addonSubscriptions.reduce((sum, addon) => sum + addon.includedSeats, 0);
  const totalSeats = totalIncludedSeats ?? (basePlanSeats + addonSeats);
  const totalMonthlyCost = basePriceCents + addonSubscriptions.reduce(
    (sum, addon) => sum + (addon.planSnapshot?.basePriceCents || 0),
    0
  );

  return (
    <>
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <Package className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t.seats.yourSubscriptionTitle}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t.seats.manageSubtitle}
              </p>
            </div>
          </div>
          <button type="button"
            onClick={() => setShowAddAddonModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t.seats.addSeatsButton}
          </button>
        </div>

        {/* Subscription Items */}
        <div className="space-y-3">
          {/* Base Plan */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                    {t.seats.basePlanBadge}
                  </span>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {baseSubscription.planSnapshot?.name ||
                      baseSubscription.planName ||
                      t.seats.standardPlanFallback}
                  </h3>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500 dark:text-gray-400">{t.seats.priceLabel}</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatAmount(basePriceCents)}/{baseSubscription.billingPeriod === 'ANNUAL' ? t.seats.perYearSuffix : t.seats.perMonthSuffix}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500 dark:text-gray-400">{t.seats.seatsLabelColon}</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {basePlanSeats}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500 dark:text-gray-400">{t.seats.sinceLabel}</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDate(baseSubscription.startedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Addon Subscriptions */}
          {addonSubscriptions.map((addon) => (
            <div
              key={addon.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                      {t.seats.addonBadge}
                    </span>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {addon.planSnapshot?.name || addon.planName}
                    </h3>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500 dark:text-gray-400">{t.seats.priceLabel}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatAmount(addon.planSnapshot?.basePriceCents || 0)}/{addon.billingPeriod === 'ANNUAL' ? t.seats.perYearSuffix : t.seats.perMonthSuffix}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500 dark:text-gray-400">{t.seats.seatsLabelColon}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {addon.includedSeats}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500 dark:text-gray-400">{t.seats.sinceLabel}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatDate(addon.planSnapshot?.purchasedAt || addon.startedAt)}
                      </span>
                    </div>
                  </div>
                </div>
                <button type="button"
                  onClick={() => handleRemoveClick(addon)}
                  className="ml-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title={t.seats.removeAddonTitle}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}

          {addonSubscriptions.length === 0 && (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              <p>{t.seats.noAdditionalSeatsMessage}</p>
              <p className="text-sm mt-1">{t.seats.clickAddSeatsHint}</p>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t.seats.totalMonthlyCostLabel}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatAmount(totalMonthlyCost)}
                </p>
              </div>
              <div className="h-10 w-px bg-gray-200 dark:bg-gray-700" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t.seats.totalSeatsLabel}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {totalSeats}
                </p>
              </div>
            </div>
            <div className="text-right text-sm text-gray-500 dark:text-gray-400">
              <p>{t.seats.fromBasePlanSuffix.replace('{n}', String(basePlanSeats))}</p>
              {addonSubscriptions.length > 0 && (
                <p>
                  {(addonSubscriptions.length === 1 ? t.seats.fromAddonsOne : t.seats.fromAddonsOther)
                    .replace('{seats}', String(addonSeats))
                    .replace('{count}', String(addonSubscriptions.length))}
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Add Addon Modal */}
      <AddAddonModal
        isOpen={showAddAddonModal}
        onClose={() => setShowAddAddonModal(false)}
      />

      {/* Remove Addon Confirmation Modal */}
      <Modal
        isOpen={showRemoveModal}
        onClose={handleCancelRemove}
        title={t.seats.removeAddonModalTitle}
        size="medium"
        confirmText={t.seats.removeButton}
        handleConfirm={handleConfirmRemove}
        isLoading={isRemoving}
        disableConfirm={!canRemoveAddon}
      >
        <div className="space-y-4">
          {addonToRemove && (
            <>
              {!canRemoveAddon ? (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        {t.seats.cannotRemoveTitle}
                      </p>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                        {t.seats.cannotRemoveBody1Prefix}{' '}
                        <strong>
                          {(activeSeats.length === 1 ? t.seats.activeUserOne : t.seats.activeUserOther).replace(
                            '{n}',
                            String(activeSeats.length),
                          )}
                        </strong>{' '}
                        {t.seats.cannotRemoveBody1Suffix}
                      </p>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                        {t.seats.cannotRemoveBody2Prefix}{' '}
                        <strong>
                          {(seatsNeededToRemove === 1 ? t.seats.userOne : t.seats.userOther).replace(
                            '{n}',
                            String(seatsNeededToRemove),
                          )}
                        </strong>{' '}
                        {t.seats.cannotRemoveBody2Suffix}{' '}
                        <a href="/users" className="underline font-medium hover:text-red-800 dark:hover:text-red-200">
                          {t.seats.usersPageLink}
                        </a>.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-gray-700 dark:text-gray-300">
                    {t.seats.confirmRemovePrefix}{' '}
                    <strong>{addonToRemove.planSnapshot?.name || addonToRemove.planName}</strong>?
                  </p>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      {(addonToRemove.includedSeats === 1
                        ? t.seats.removeSeatWarningOne
                        : t.seats.removeSeatWarningOther
                      ).replace('{n}', String(addonToRemove.includedSeats))}
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
                      {t.seats.removeAddonFinalNote}
                    </p>
                  </div>
                </>
              )}
              {removeError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    {removeError}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default OrganizationSeats;
